import * as d3 from "d3";
import {scaleBand, scaleLinear} from "d3";
import React, {useEffect, useRef} from "react";
import type {AdvancedSpendData} from "../../../services/ApiTypes";
import "./AdvancedBarChart.scss";
import {CircularProgress, Grid, TablePagination} from "@mui/material";
import type {HashedLoadingDataWrapper} from "../../../utils/LoadingDataWrapper";
import {observer} from "mobx-react-lite";
import {toCurrencyWithP} from "../../currency-component/CurrencyClasses";
import {useStores} from "../../../stores";
import type {SelectionResult} from "../api-dropdown-filter/ApiDropdownTypes";
import {MultiSelection} from "../api-dropdown-filter/ApiDropdownTypes";

const FONT_SIZE = 14;
const TOOLTIP_FONT_SIZE = 20;
const BAR_PADDING = 0.25;

export const AdvancedBarChart: React.FC<{
    dataWrapper: HashedLoadingDataWrapper<AdvancedSpendData>,
    selection?: SelectionResult<string>,
    onSelectionChange: (selection: SelectionResult<string>) => void,
    pageSize?: number,
    pageSizeOptions?: Array<number>,
    columnBreakDown?: number,
    loading?: boolean,
    /** Height of the chart area in px (default 415, or 300 when split in columns) */
    chartHeight?: number,
    /** Bar colour (default navy) */
    barColor?: string,
    /** When given, the long tail (the bars after the first 80% of spend) gets this colour */
    tailColor?: string,
}> = observer(({
                   dataWrapper,
                   selection,
                   onSelectionChange,
                   pageSize,
                   pageSizeOptions,
                   columnBreakDown,
                   loading,
                   chartHeight,
                   barColor = '#193150',
                   tailColor,
               }) => {
    const {p} = useStores();
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(pageSize || 50);
    const [rowsPerPageOptions] = React.useState(pageSizeOptions || [5, 12, 25, 50, {value: -1, label: 'All'}]); // Maybe add all

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const svgRef = useRef<SVGSVGElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);


    const margin = {top: FONT_SIZE / 2 + 1, right: 0, bottom: FONT_SIZE / 2 + 1, left: 60};
    // CAT-2195 Assumption about width here is based on a FHD screen size, and should work with multiple column sizes
    const height = (chartHeight ?? (columnBreakDown ? 300 : 415)) - margin.top - margin.bottom;
    columnBreakDown = columnBreakDown || 2;
    const width = (1400 / columnBreakDown) - margin.left - margin.right;
    let data = dataWrapper.elements;
    const datahash = dataWrapper.hash ? (dataWrapper.hash + '-' + rowsPerPage) : '';
    // CAT-3185: new data (e.g. after a reset) can have fewer pages than the page we were on
    if (page > 0 && rowsPerPage > 0 && page * rowsPerPage >= data.length) {
        setPage(0);
    }

    useEffect(() => {
        if (!svgRef.current) {
            return;
        }
        if (!datahash) {
            return;
        }
        if (svgRef.current.dataset['datahash'] === datahash && svgRef.current.dataset['page'] === page.toString()) {
            // Don't re-render if the input data is already rendered
            return;
        }
        svgRef.current.dataset['datahash'] = datahash;
        svgRef.current.dataset['page'] = page.toString();

        const svg = d3.select(svgRef.current);
        const tooltip = d3.select(tooltipRef.current);

        svg.html("");
        svg
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom);

        tooltip
            .style("opacity", 0);

        // The suppliers that together make up the first 80% of the positive spend (of all pages)
        const headIds = new Set<string>();
        if (tailColor) {
            const totalSpend = data.reduce((sum, d) => sum + Math.max(0, d.spend), 0);
            let cumulative = 0;
            for (const d of [...data].sort((a, b) => b.spend - a.spend)) {
                if (cumulative >= 0.8 * totalSpend) break;
                headIds.add(d.s_id);
                cumulative += Math.max(0, d.spend);
            }
        }

        const showAll = rowsPerPage <= 0;
        data = showAll ? data : data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

        function mouseover(this: any) {
            tooltip.style("opacity", 1);
            d3.select(this).select('.bar').attr('fill-opacity', 0.5);
        }

        function mousemove(this: any, event: MouseEvent, d: AdvancedSpendData) {
            tooltip.text(d.group + ": " + toCurrencyWithP(d.spend, p.currencyFormat, p.currencySymbol))
                .style("font-size", TOOLTIP_FONT_SIZE + "px")
                .style("left", event.pageX + "px")
                .style("top", (event.pageY + TOOLTIP_FONT_SIZE) + "px");
        }

        function mouseleave(this: any) {
            tooltip.style("opacity", 0);
            d3.select(this).select('.bar').attr('fill-opacity', 1);
        }

        function click(this: Element, event: MouseEvent, d: AdvancedSpendData) {
            if (event.ctrlKey) {
                const newSelection = MultiSelection.exclusiveToggle(selection, d.s_id);
                if (newSelection) {
                    onSelectionChange(newSelection)
                }
            } else {
                onSelectionChange(MultiSelection.inclusiveToggle(selection, d.s_id))
            }
        }

        const scaleX = scaleBand()
            .domain(data.map(({group}) => group))
            .range([0, width])
            .padding(BAR_PADDING);
        const bandWidth = scaleX.bandwidth();
        const spends = data.map(({spend}) => spend);
        const scaleY = scaleLinear()
            .domain([Math.min(0, Math.min(...spends)), Math.max(...spends)])
            .range([height, 0]);

        // Add X axis
        svg.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(${margin.left}, ${height + margin.top})`)
            .call(d3.axisBottom(scaleX).tickFormat(() => '').tickSize(0));

        // Add Y axis
        svg.append("g")
            .attr("class", "y-axis")
            .style("font-size", FONT_SIZE + 'px')
            .attr("transform", `translate(${margin.left}, ${margin.top})`)
            .call(d3.axisLeft(scaleY).ticks(5).tickFormat(v => d3.format("~s")(v)));


        const barWrapper = svg.selectAll(".bar")
            .data(data.values())
            .enter()
            .append("g")
            .classed("bar-wrapper", true)
            .attr("style", "cursor: pointer;")
            .attr('transform', (d: AdvancedSpendData) => `translate(${margin.left + (scaleX(d.group) as any)}, ${margin.top})`)
            .on("mouseover", mouseover)
            .on("mousemove", mousemove)
            .on("mouseleave", mouseleave)
            .on("click", click);

        barWrapper
            .append("rect")
            .classed("bar-background", true)
            .attr("style", "fill: transparent;")
            .attr("width", scaleX.step())
            .attr("height", height)

        barWrapper
            .append("rect")
            .classed("bar", true)
            .classed("negative-bar", (d) => d.spend < 0)
            .attr("key", (d) => `bar-${d.group}`)
            .attr("x", 0)
            .attr("y", (d: AdvancedSpendData) => d.spend > 0 ? scaleY(d.spend) : scaleY(0))
            .attr("width", bandWidth)
            .attr("height", (d) => d.spend > 0 ? scaleY(0) - scaleY(d.spend) : scaleY(d.spend) - scaleY(0))
            .attr("fill-opacity", 1)
            .attr("fill", (d: AdvancedSpendData) => !tailColor || headIds.has(d.s_id) ? barColor : tailColor)

        // //DEBUG: show margins
        // svg.append('rect')
        //     .attr('x', 0)
        //     .attr('y', 0)
        //     .attr('width', width)
        //     .attr('height', height);
        //
        // svg.append('rect')
        //     .attr('x', 0)
        //     .attr('y', 0)
        //     .attr("transform", `translate(${margin.left}, ${margin.top})`)
        //     .attr('width', width-margin.left)
        //     .attr('height', height-margin.top)
        //     .attr('fill', 'red')
        },
        // Note: We need to response to `loading` as well to ensure the svgRef can be found again
        [page, data, datahash, loading]);

    // return <div className="advanced-bar-chart-container"  style={{height: height + 'px'}}>
    // return <div className="advanced-bar-chart-container" style={{width: '100%', height: '600px'}}>
    return <div className="advanced-bar-chart-container"
                style={{width: '100%', height: height + 100 + 'px', overflow: 'clip'}}>
        {loading ? (
            <Grid container justifyContent={'center'} alignItems={'center'}
                  style={{width: width + 'px', height: height + 'px'}}>
                <CircularProgress color="inherit"/>
            </Grid>
        ) : (<>
            <div className="tooltip" ref={tooltipRef}/>
            <svg
                className="advanced-bar-chart"
                ref={svgRef}/>
            <table>
                <tbody>
                <tr>
                    <TablePagination
                        colSpan={data.length}
                        count={data?.length || 0}
                        page={page}
                        showFirstButton={true}
                        showLastButton={true}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={rowsPerPageOptions}
                        labelRowsPerPage="Suppliers in view"
                        SelectProps={{
                            inputProps: {'aria-label': 'Suppliers in view'},
                            native: true,
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </tr>
                </tbody>
            </table>
        </>)}
    </div>;
});
